import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Chip, EmptyState, InlineError, Input, Skeleton } from '../../../components/ui'
import { effectivePrice } from '../../../features/catalog'
import { addService, removeService } from '../../../features/booking/selectionSlice'
import { useGetServicesQuery } from '../../../services/catalogApi'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useTenant } from '../../../tenant/useTenant'
import { cn } from '../../../utils/cn'
import { formatDuration, formatPrice } from '../../../utils/format'
import { useWizardAction } from '../wizardContext'
import type { Service } from '../../../types/storefront'

/** One page holds the whole bookable menu: the wizard filters and groups in the
 *  browser so adding a service never costs a round trip. */
const PER_PAGE = 200

const UNCATEGORISED = 'Other services'

export function ServicesStep() {
  const { slug, bootstrap } = useTenant()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const selection = useAppSelector((s) => s.selection)
  const [term, setTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)

  const branchId = selection.branchId
  const query = useGetServicesQuery(
    { slug, branchId, perPage: PER_PAGE },
    { skip: branchId === null },
  )

  const services = useMemo(() => query.data?.items ?? [], [query.data])

  const categories = useMemo(() => {
    const names = new Set<string>()
    for (const s of services) names.add(s.category_name ?? UNCATEGORISED)
    return [...names]
  }, [services])

  const groups = useMemo(() => {
    const needle = term.trim().toLowerCase()
    const buckets = new Map<string, Service[]>()
    for (const service of services) {
      const name = service.category_name ?? UNCATEGORISED
      if (categoryFilter && name !== categoryFilter) continue
      if (needle && !`${service.name} ${service.description ?? ''}`.toLowerCase().includes(needle)) continue
      const bucket = buckets.get(name)
      if (bucket) bucket.push(service)
      else buckets.set(name, [service])
    }
    return [...buckets.entries()]
  }, [services, term, categoryFilter])

  const chosenIds = useMemo(() => new Set(selection.services.map((s) => s.id)), [selection.services])
  const count = selection.services.length

  const toggle = (service: Service) => {
    if (chosenIds.has(service.id)) {
      dispatch(removeService(service.id))
      return
    }
    dispatch(
      addService({
        id: service.id,
        name: service.name,
        duration: service.duration,
        price: effectivePrice(service),
        staffId: null,
        staffName: null,
        favourite: false,
      }),
    )
  }

  useWizardAction(
    { label: 'Continue', onClick: () => navigate('../specialist'), disabled: count === 0 },
    [count, navigate],
  )

  return (
    <div className="container-page py-12 md:py-section-gap-md">
      <header className="mb-12 max-w-2xl">
        <h1 className="mb-4 font-display text-headline-lg-mobile text-primary md:text-headline-lg">
          Curate your experience
        </h1>
        <p className="font-body-lg text-body-lg text-on-surface-variant">
          Pick one or more. The total duration and price update below as you go.
        </p>
      </header>

      <div className="mb-12 flex flex-col gap-6">
        <Input
          label="Search services"
          type="search"
          value={term}
          leadingIcon="search"
          placeholder="Try haircut or blow dry"
          onChange={(e) => setTerm(e.target.value)}
        />
        {categories.length > 1 && (
          <div className="flex gap-3 overflow-x-auto pb-1 no-scrollbar">
            <Chip selected={categoryFilter === null} onClick={() => setCategoryFilter(null)}>
              All
            </Chip>
            {categories.map((name) => (
              <Chip
                key={name}
                selected={categoryFilter === name}
                onClick={() => setCategoryFilter(categoryFilter === name ? null : name)}
              >
                {name}
              </Chip>
            ))}
          </div>
        )}
      </div>

      {count > 0 && (
        <section aria-label="Selected services" className="mb-12 flex flex-wrap gap-3">
          {selection.services.map((service) => (
            <Chip key={service.id} selected onRemove={() => dispatch(removeService(service.id))}>
              {service.name}
            </Chip>
          ))}
        </section>
      )}

      {query.isLoading ? (
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} shape="rect" className="h-56 w-full" />
          ))}
        </div>
      ) : query.error ? (
        <InlineError error={query.error} onRetry={query.refetch} className="mb-8" />
      ) : groups.length === 0 ? (
        <EmptyState
          icon="search_off"
          title="No services match your search"
          body="Try a different word, or clear the category filter to see the whole menu."
          action={
            <Button
              variant="outlined"
              onClick={() => {
                setTerm('')
                setCategoryFilter(null)
              }}
            >
              Clear filters
            </Button>
          }
        />
      ) : (
        // Stitch separates categories by a full section gap — that whitespace is
        // what makes the menu read as a gallery rather than a price list.
        <div className="flex flex-col gap-16 md:gap-section-gap-md">
          {groups.map(([name, items]) => (
            <section key={name}>
              <h2 className="mb-8 border-b border-primary/10 pb-4 font-display text-headline-sm text-primary">
                {name}
              </h2>
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                {items.map((service) => {
                  const active = chosenIds.has(service.id)
                  return (
                    <article
                      key={service.id}
                      className={cn(
                        'flex flex-col rounded-lg border p-6 transition-colors duration-300',
                        active
                          ? 'border-primary bg-primary/5'
                          : 'border-outline-variant bg-surface-container-lowest hover:bg-surface-container-low',
                      )}
                    >
                      <div className="mb-4 flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          {/* label-caps -> headline-sm -> body-md is the card
                              hierarchy the design system spells out. */}
                          <span className="mb-1 block font-label-caps text-label-caps uppercase text-on-surface-variant">
                            {formatDuration(service.duration)}
                          </span>
                          <h3 className="font-display text-headline-sm text-on-surface">{service.name}</h3>
                        </div>
                        <span className="shrink-0 font-display text-headline-sm text-primary">
                          {formatPrice(effectivePrice(service), bootstrap.currency)}
                        </span>
                      </div>
                      {service.description && (
                        <p className="mb-6 line-clamp-3 font-body-md text-body-md text-on-surface-variant">
                          {service.description}
                        </p>
                      )}
                      <div className="mt-auto">
                        <Button
                          size="lg"
                          variant={active ? 'filled' : 'outlined'}
                          fullWidth
                          icon={active ? 'check' : 'add'}
                          onClick={() => toggle(service)}
                        >
                          {active ? 'Added' : 'Select'}
                        </Button>
                      </div>
                    </article>
                  )
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
